import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import usePackageOptions from '../../hooks/usePackageOptions';
import { HeartIcon } from '../PortraitsGallery/icons';
import offeredOptions, { priceOf } from './offeredOptions';
import discountedPrice, { money, orderCredit } from '../../utils/offerPricing';
import PromoBanner from '../PromoBanner';
import { useCustomer } from '../../contexts/CustomerContext';
import AbsolutePosImgs from '../_pages/AbsolutePosImgs/AbsolutePosImgs';
import defaultBackground from '../../assets/Package_Blue.png';

/**
 * One package card. A subcomponent (not inline in the grid's .map) BECAUSE it calls
 * AbsolutePosImgs, which runs hooks — a per-row component is the only way those hooks
 * stay valid across renders.
 *
 * The preview mirrors PackageDisplay exactly: the pricelist artwork is the frame/
 * mockup, and AbsolutePosImgs composites THIS student's own poses into it (the code
 * that already "handles that"). It returns null for a package with no preview layout,
 * leaving just the artwork (e.g. the All-Digital phone banner).
 */
function PackageCard({
  option, offer, packageId, onSelect,
}) {
  const { customer } = useCustomer();
  const price = priceOf(option);
  // Percentage offers get an honest per-card was/now; the flat part of a combined
  // offer is an order-level credit, shown once below (not baked into the price).
  // By option id: a per-package offer may give this card its own percent.
  const discounted = discountedPrice(price, offer, option.id);
  const credit = orderCredit(offer);
  const baseImage = option.imageUrl && !option.imageUrl.includes('missing')
    ? option.imageUrl
    : defaultBackground;

  return (
    // The package artwork carries its OWN rounded corners. Keep bg-white OFF the image
    // area so those corners fall on the gray page (like undergrad) instead of a white
    // sliver poking past the graphic; white lives only under the info section below.
    <div className="flex flex-col rounded-b-2xl shadow-sm">
      <div className="relative w-full overflow-hidden">
        <img src={baseImage} alt={option.name || 'Package'} className="block w-full" />
        {AbsolutePosImgs(customer, packageId, option)}
      </div>

      <div className="flex flex-1 flex-col p-4 bg-white rounded-b-2xl">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-bold text-gray-900">
            {option.name || option.abbreviation}
          </h2>
          {/* Prices stacked, right-aligned: discounted bold on top, struck regular below.
              The offer is stated once in the banner, not repeated per card. */}
          <span className="flex flex-col items-end whitespace-nowrap leading-tight">
            <span className="text-xl font-extrabold text-gray-900">
              {money(discounted != null ? discounted : price)}
            </span>
            {discounted != null && (
              <span className="text-sm font-semibold text-gray-500 line-through decoration-2 decoration-gray-700">
                {money(price)}
              </span>
            )}
            {credit != null && (
              // The flat part is taken once off the whole order, so it's labeled
              // "at checkout" rather than folded into the per-card price.
              <span className="mt-0.5 text-xs font-semibold text-green-700">
                {`+ ${money(credit)} credit at checkout`}
              </span>
            )}
          </span>
        </div>

        {option.description && (
          <p className="mt-2 text-sm text-gray-600">{option.description}</p>
        )}

        <button
          type="button"
          onClick={() => onSelect(option)}
          className="mt-4 w-full rounded-lg bg-shoob-blue py-2.5 font-semibold text-white shadow hover:bg-shoob-blueDark"
        >
          Select
        </button>
      </div>
    </div>
  );
}

PackageCard.defaultProps = { offer: null };

PackageCard.propTypes = {
  // Spraypaint Option model.
  // eslint-disable-next-line react/forbid-prop-types
  option: PropTypes.object.isRequired,
  packageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSelect: PropTypes.func.isRequired,
  offer: PropTypes.shape({
    code: PropTypes.string,
    couponType: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    flatValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

/**
 * "Choose your package" — screen 2, for every audience.
 *
 * ONE implementation, parameterized, exactly like PortraitsGallery: the audiences differ
 * only in which options are offered and whether there's a way out of buying prints.
 *
 *   seniors  -> `excludeOption` drops the standalone $0 yearbook Option (it's its own
 *               step now), and `skip` offers "just my yearbook photo" -> checkout.
 *   everyone -> neither prop: every priced option on the pricelist, no skip.
 *
 * The pricelist drives the content. An option with no price for this school is not
 * offered — it is not buyable here, and a card with no price is a dead end.
 */
export default function PackageChooser({
  packageId,
  schoolId,
  headline,
  subline,
  excludeOption,
  onSelect,
  skip,
  offer,
}) {
  const { data: options, isFetching } = usePackageOptions(packageId, schoolId);

  // What this pricelist offers as packages, in order. Rules live in offeredOptions so
  // they can be tested — they decide what a customer can buy.
  const offered = useMemo(
    () => offeredOptions(options, excludeOption),
    [options, excludeOption],
  );

  // usePackageOptions sets placeholderData: [], so isLoading reads false from the first
  // render — gate on isFetching (the real in-flight signal) instead, or the empty
  // placeholder flashes the "no packages available" state before the fetch resolves.
  if (isFetching) return null;

  return (
    // Full-width gray page (#e4e4e4) matching the undergrad OptionSelection page, so each
    // white PackageCard reads as a defined card against the gray.
    <div className="bg-[#e4e4e4] min-h-screen pb-16">
      {/* Promotional offer banner FIRST — the customer was emailed/texted a special
          offer, so it opens the page (designer, 07-22). Shared with elementary. It is
          full-bleed + sticky, so it stays OUTSIDE the centered content wrapper below. */}
      <PromoBanner offer={offer} />

      {/* Centered content column on the gray page. */}
      <div className="mx-auto max-w-6xl px-4">
        {/* Headline is optional; the senior flow dropped its "Choose your package". */}
        {headline && (
          <div className="bg-white py-8 text-center rounded-b-lg shadow-sm">
            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">{headline}</h1>
          </div>
        )}

        {subline && (
          <div className="mt-2 bg-shoob-blue text-white rounded-md px-5 py-4 flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/20">
              <HeartIcon filled className="h-5 w-5" />
            </span>
            <p className="text-lg leading-tight font-semibold">{subline}</p>
          </div>
        )}

        {offered.length === 0 ? (
          // Never a blank screen: a pricelist with no priced options is a setup problem,
          // and the parent needs to be told rather than left staring at nothing.
          <p className="mt-8 text-center text-gray-600">
            No packages are available for your school yet. Please check back soon.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:gap-8 mt-5 max-w-lg mx-auto">
            {offered.map((option) => (
              <PackageCard
                key={option.id}
                option={option}
                offer={offer}
                packageId={packageId}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}

        {skip && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={skip.onClick}
              className="text-shoob-blue hover:text-shoob-blueDark font-semibold underline underline-offset-4"
            >
              {skip.label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

PackageChooser.defaultProps = {
  headline: null,
  subline: null,
  excludeOption: null,
  skip: null,
  offer: null,
};

PackageChooser.propTypes = {
  packageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  headline: PropTypes.string,
  subline: PropTypes.string,
  // (option) => bool. Seniors drop the standalone $0 yearbook Option, which is its own step.
  excludeOption: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  // A way out of buying prints, e.g. the senior "just my yearbook photo" path.
  skip: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
  // Resolved promotional offer coupon (from useOfferCoupon) — drives the banner
  // and the per-card struck price. Null when no offer is live.
  offer: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
    couponType: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    promoHeadline: PropTypes.string,
    promoSubhead: PropTypes.string,
  }),
};
